<template>
  <div>
    <!-- Homework Details -->
    <b-card class="mb-2">
      <h5 class="mb-2">تفاصيل الواجب</h5>
      <p><strong>الوصف:</strong> {{ activity.assignment_and_projects_data.description }}</p>
      <p><strong>نوع المحتوى:</strong> {{ activity.assignment_and_projects_data.type }}</p>
      <p><strong>حجم الملف المسموح:</strong> {{ activity.assignment_and_projects_data.file_size }}</p>
      <p><strong>عدد الملفات المسموحة:</strong> {{ activity.assignment_and_projects_data.max_files_allowed_count }}</p>
    </b-card>

    <!-- Submission Form -->
    <b-card>
      <h5 class="mb-2">تقديم الإجابة</h5>
      
      <b-form @submit.prevent="submitHomework">
        <!-- Text Answer -->
        <b-form-group label="الإجابة النصية" label-for="text-answer">
          <ckeditor
            id="text-answer"
            v-model="submission.textAnswer"
            :config="{ contentsLangDirection: 'rtl', versionCheck: false }"
            :placeholder="'اكتب إجابتك هنا...'"
          />
        </b-form-group>

        <!-- File Upload -->
        <b-form-group label="إرفاق ملفات *" label-for="file-upload">
          <b-form-file
            id="file-upload"
            v-model="submission.files"
            :placeholder="'اختر ملفات... (الحد الأقصى: ' + activity.assignment_and_projects_data.max_files_allowed_count + ')'"
            :drop-placeholder="'اسحب الملفات هنا...'"
            multiple
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.zip,.rar"
            @change="handleFileChange"
          />
          <small class="text-muted d-block mt-1">
            الحد الأقصى لحجم الملف: {{ activity.assignment_and_projects_data.file_size }}
          </small>
          <small class="text-muted d-block">
            عدد الملفات المسموحة: {{ activity.assignment_and_projects_data.max_files_allowed_count }}
          </small>
        </b-form-group>

        <!-- Selected Files Preview -->
        <div v-if="submission.files && submission.files.length > 0" class="mb-2">
          <h6>الملفات المختارة:</h6>
          <b-list-group>
            <b-list-group-item
              v-for="(file, index) in submission.files"
              :key="index"
              class="d-flex justify-content-between align-items-center">
              <div>
                <feather-icon icon="FileIcon" class="mr-50" />
                <span>{{ file.name }}</span>
                <small class="text-muted ml-1">({{ formatFileSize(file.size) }})</small>
              </div>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="removeFile(index)">
                <feather-icon icon="XIcon" size="16" />
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </div>

        <!-- Submit Button -->
        <b-row>
          <b-col cols="12" md="6">
            <b-button
              type="submit"
              variant="primary"
              block
              :disabled="submitting">
              <b-spinner v-if="submitting" small class="mr-50" />
              <feather-icon v-else icon="SendIcon" class="mr-50" />
              {{ submitting ? 'جاري التقديم...' : 'تقديم الإجابة' }}
            </b-button>
          </b-col>
          <b-col cols="12" md="6" class="mt-1 mt-md-0">
            <b-button variant="outline-secondary" block @click="$router.go(-1)">
              <feather-icon icon="XIcon" class="mr-50" />
              إلغاء
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import ActivitiesController from "@/Controllers/ActivitiesController";

export default {
  props: {
    activity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      submitting: false,
      submission: {
        textAnswer: "",
        files: [],
      },
    };
  },
  methods: {
    handleFileChange(event) {
      const files = Array.from(event.target.files || []);
      const maxFiles = this.activity.assignment_and_projects_data.max_files_allowed_count;
      const maxSizeBytes = this.parseFileSize(this.activity.assignment_and_projects_data.file_size);

      // Check file count
      if (files.length > maxFiles) {
        Swal.fire({
          icon: "warning",
          title: "تحذير",
          text: `يمكنك رفع ${maxFiles} ملفات كحد أقصى`,
        });
        this.submission.files = [];
        event.target.value = '';
        return;
      }

      // Check file sizes
      const oversizedFiles = files.filter(file => file.size > maxSizeBytes);
      if (oversizedFiles.length > 0) {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: `بعض الملفات تتجاوز الحد المسموح (${this.activity.assignment_and_projects_data.file_size}). الملفات المرفوضة: ${oversizedFiles.map(f => f.name).join(', ')}`,
        });
        this.submission.files = [];
        event.target.value = '';
        return;
      }

      this.submission.files = files;
    },

    parseFileSize(sizeString) {
      // Parse sizes like "100KB", "10MB", etc.
      const match = sizeString.match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)$/i);
      if (!match) return 1024 * 1024; // Default 1MB
      
      const value = parseFloat(match[1]);
      const unit = match[2].toUpperCase();
      
      const multipliers = {
        'KB': 1024,
        'MB': 1024 * 1024,
        'GB': 1024 * 1024 * 1024,
      };
      
      return value * (multipliers[unit] || 1024);
    },

    removeFile(index) {
      this.submission.files.splice(index, 1);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    async submitHomework() {
      // Manual validation - require at least one file
      if (!this.submission.files || this.submission.files.length === 0) {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: "يرجى رفع ملف واحد على الأقل",
        });
        return;
      }

      try {
        this.submitting = true;

        // Create FormData
        const formData = new FormData();
        formData.append('type', 'homework');
        formData.append('activity_id', this.activity.id);
        formData.append('text_answer', this.submission.textAnswer);
        
        if (this.submission.files && this.submission.files.length > 0) {
          this.submission.files.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
          });
        }

        // Submit to API
        const activitiesController = new ActivitiesController();
        const result = await activitiesController.submitActivityForStudent(formData);


        // Check if submission was successful
        if (result && result.success) {
          Swal.fire({
            icon: "success",
            title: "تم التقديم بنجاح",
            text: result.message || "تم تقديم إجابتك بنجاح",
            timer: 2000,
            showConfirmButton: false,
          });
          
          // Redirect immediately
          setTimeout(() => {
            this.$router.push({
              name: "activities-v2-detail",
              params: { id: this.activity.id },
            });
          }, 500);
        } else {
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text: result.message || "فشل في تقديم الإجابة",
            showConfirmButton: false,
          });
        }
      } catch (error) {
      
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: error.response?.data?.message || "حدث خطأ أثناء تقديم الإجابة",
          showConfirmButton: false,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.list-group-item {
  padding: 0.75rem 1rem;
}
</style>

